// src/App.js - COMPLETE FIXED VERSION
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';

// Privacy & Cookie Components
import CookieConsent from './components/privacy/CookieConsent';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import TermsOfService from './components/legal/TermsOfService';

// Main Components
import VerifyEmail from './components/auth/VerifyEmail';
import Header from './components/header/Header';
import Home from './components/home/Home';
import About from './components/about/About';
import Skills from './components/skills/Skills';
import Services from './components/services/Services';
import Qualification from './components/qualification/Qualification';
import Contact from './components/contact/Contact';
import Footer from './components/footer/Footer';
import ScrollUp from './components/scrollup/ScrollUp';
import Blog from './components/blog/Blog';
import Newsletter from './components/newsletter/Newsletter';
import LatestApps from './components/apps/LatestApps';
import Success from './components/success/Success';
import MagicLink from './pages/MagicLink';
import WelcomeDashboard from './components/dashboard/WelcomeDashboard';
import EmailVerification from './pages/EmailVerification';

// User Profile Component for logged-in clients
import UserProfile from './components/profile/UserProfile';

// Dark Mode & Live Chat Components
import DarkModeToggle from './components/darkmode/DarkModeToggle';
import LiveChat from './components/livechat/LiveChat';

// Auth Components
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import GoodbyePopup from './components/auth/GoodbyePopup';

// Dashboard Components
import ClientDashboard from './components/dashboard/ClientDashboard';
import AdminDashboard from './components/admin/AdminDashboard';

// Note: Ensure these component files exist in your project:
// - src/components/privacy/CookieConsent.jsx
// - src/components/legal/PrivacyPolicy.jsx
// - src/components/legal/TermsOfService.jsx
// - src/components/profile/UserProfile.jsx
// - src/components/darkmode/DarkModeToggle.jsx
// - src/components/livechat/LiveChat.jsx
// - src/components/success/Success.jsx
// - src/pages/MagicLink.jsx
// - src/components/dashboard/WelcomeDashboard.jsx
// - src/pages/EmailVerification.jsx
// - src/components/admin/AdminDashboard.jsx

// ✅ FIXED: Use environment variable for API URL
// Dev-only: switch off CRA's runtime-error overlay so the Cal.com embed's benign
// cross-origin "Script error." stops covering the screen. The overlay is a
// development feature only and never exists in your production build, so this
// changes nothing for clients. Real errors still print to the browser console.
if (process.env.NODE_ENV === 'development') {
  import('react-error-overlay')
    .then((m) => {
      if (m && typeof m.stopReportingRuntimeErrors === 'function') {
        m.stopReportingRuntimeErrors();
      }
    })
    .catch(() => {});
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Import GoodbyePopup component
// Make sure to create: src/components/auth/GoodbyePopup.jsx and GoodbyePopup.css

// Scrolls to the #hash target on load / navigation (e.g. "/#contact" coming
// from the dashboard). React Router doesn't do this natively, so we retry
// briefly until the target section has actually mounted.
function ScrollToHash() {
  const { hash, pathname } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    let tries = 0;
    const tick = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (tries++ < 25) {
        setTimeout(tick, 80);
      }
    };
    tick();
  }, [hash, pathname]);
  return null;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGoodbye, setShowGoodbye] = useState(false);
  const [logoutUserName, setLogoutUserName] = useState('');

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    const userInfo = localStorage.getItem('userInfo');
    
    if (!token || !userInfo) {
      setLoading(false);
      return;
    }

    // First, immediately set auth from cached data (prevents flash of logged-out state)
    try {
      const cachedUser = JSON.parse(userInfo);
      setIsAuthenticated(true);
      setUser(cachedUser);
    } catch (parseError) {
      console.error('Error parsing cached user:', parseError);
      clearAuthSilently();
      setLoading(false);
      return;
    }

    // Then verify with backend in background (optional - for token refresh/validation)
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Update with fresh data from server
        const freshUser = data.user || data;
        setUser(freshUser);
        localStorage.setItem('userInfo', JSON.stringify(freshUser));
        console.log('Auth verified with backend - User role:', freshUser.role);
      } else if (response.status === 401) {
        // Token expired or invalid - clear silently (no goodbye popup)
        console.log('Token expired, clearing auth silently');
        clearAuthSilently();
      }
      // For other errors (500, network), keep cached auth
    } catch (error) {
      // Network error or backend down - keep using cached auth
      console.log('Backend unreachable, using cached auth');
    }
    
    setLoading(false);
  };

  // Silent auth clear (for expired tokens, page refresh issues)
  const clearAuthSilently = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('user');
    sessionStorage.removeItem('pendingPackage');
    setIsAuthenticated(false);
    setUser(null);
  };

  // User-initiated logout (shows goodbye popup)
  const handleLogout = () => {
    // Get user name before clearing
    const userName = user?.firstName || user?.profile?.firstName || 'Friend';
    setLogoutUserName(userName);
    
    // Show goodbye popup
    setShowGoodbye(true);
    
    // Clear all possible token locations
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('user');
    sessionStorage.removeItem('pendingPackage');
    
    // Reset state
    setIsAuthenticated(false);
    setUser(null);
    
    // Redirect to HOME after 3 seconds (not login)
    setTimeout(() => {
      setShowGoodbye(false);
      window.location.href = '/';
    }, 3000);
  };

  // ✅ FIXED: Developer About Section - passes isClient prop to prevent duplicate title
  const DeveloperAboutSection = () => (
    <section className="about section" id="about">
      <h2 className="section__title">Meet Your Developer</h2>
      <span className="section__subtitle">The expertise behind your success</span>
      <About isClient={true} />
    </section>
  );

  // Main Layout Component - Decides which layout to show based on user status
  const MainLayout = () => {
    // If user is logged in and NOT an admin, show personalized layout
    if (isAuthenticated && user?.role !== 'admin') {
      return (
        <>
          <Header 
            isAuthenticated={isAuthenticated} 
            user={user} 
            onLogout={handleLogout} 
          />
          <main className="main">
            {/* Skip Home and Services sections for logged-in clients */}
            <UserProfile user={user} />
            <DeveloperAboutSection />
            <Skills />
            <LatestApps />
            <Blog />
            <Newsletter />
            <Qualification /> 
            <Contact />
          </main>
          <Footer />
          <ScrollUp />
          <CookieConsent />
          <DarkModeToggle />
          <LiveChat />
        </>
      );
    }
    
    // Public Layout for NON-LOGGED-IN users and ADMIN users
    return (
      <>
        <Header 
          isAuthenticated={isAuthenticated} 
          user={user} 
          onLogout={handleLogout} 
        />
        <main className="main">
          <Home isAuthenticated={isAuthenticated} />
          <Services />
          <About />
          <Skills />
          <LatestApps />
          <Blog />
          <Newsletter />
          <Qualification /> 
          <Contact />
        </main>
        <Footer />
        <ScrollUp />
        <CookieConsent />
        <DarkModeToggle />
        <LiveChat />
      </>
    );
  };

  // Legal Pages Layout
  const LegalLayout = ({ children }) => (
    <>
      <Header 
        isAuthenticated={isAuthenticated} 
        user={user} 
        onLogout={handleLogout} 
      />
      <main className="main" style={{ paddingTop: '5rem' }}>
        {children}
      </main>
      <Footer />
      <ScrollUp />
      <DarkModeToggle />
    </>
  );

  // Protected Route Component with role handling
  const ProtectedRoute = ({ children, requireAdmin = false, requireClient = false }) => {
    if (loading) {
      return (
        <div className="loading-container" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '24px'
        }}>
          <div>Loading...</div>
        </div>
      );
    }
    
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }
    
    // Admin trying to access client-only routes
    if (requireClient && user?.role === 'admin') {
      return <Navigate to="/admin" replace />;
    }
    
    // Client trying to access admin-only routes
    if (requireAdmin && user?.role !== 'admin') {
      return <Navigate to="/dashboard" replace />;
    }
    
    return children;
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="loading-container" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '24px'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <Router>
      <ScrollToHash />
      <Routes>
        {/* Main Website Route */}
        <Route path="/" element={<MainLayout />} />
        
        {/* Legal Pages */}
        <Route path="/privacy" element={
          <LegalLayout><PrivacyPolicy /></LegalLayout>
        } />
        <Route path="/privacy-policy" element={
          <LegalLayout><PrivacyPolicy /></LegalLayout>
        } />
        <Route path="/terms" element={
          <LegalLayout><TermsOfService /></LegalLayout>
        } />
        <Route path="/terms-of-service" element={
          <LegalLayout><TermsOfService /></LegalLayout>
        } />
        <Route path="/acceptable-use" element={
          <LegalLayout><TermsOfService /></LegalLayout>
        } />
        <Route path="/verify-email" element={<VerifyEmail setIsAuthenticated={setIsAuthenticated} setUser={setUser} />} />

        
        {/* Success Page */}
        <Route path="/success" element={<Success />} />
        
        {/* Authentication Routes */}
        <Route path="/login" element={
          isAuthenticated ? (
            user?.role === 'admin' ? (
              <Navigate to="/admin" replace />
            ) : (
              <Navigate to="/dashboard" replace />
            )
          ) : (
            <Login 
              setIsAuthenticated={setIsAuthenticated} 
              setUser={setUser} 
            />
          )
        } />
        
        <Route path="/register" element={
          isAuthenticated ? (
            user?.role === 'admin' ? (
              <Navigate to="/admin" replace />
            ) : (
              <Navigate to="/dashboard/welcome" replace />
            )
          ) : (
            <Register 
              setIsAuthenticated={setIsAuthenticated}
              setUser={setUser}
            />
          )
        } />
        
        {/* Email Verification Routes */}
        <Route path="/auth/magic-link" element={<MagicLink />} />
       
        {/* Password Reset Routes */}
        <Route path="/forgot-password" element={<ForgotPassword />} />
       <Route path="/reset-password" element={<ResetPassword />} />

        {/* Dashboard Route - redirects based on role */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            {user?.role === 'admin' ? (
              <Navigate to="/admin" replace />
            ) : (
              <ClientDashboard 
                user={user} 
                onLogout={handleLogout} 
              />
            )}
          </ProtectedRoute>
        } />
        
        {/* Welcome dashboard for new users */}
        <Route path="/dashboard/welcome" element={
          <ProtectedRoute>
            {user?.role === 'admin' ? (
              <Navigate to="/admin" replace />
            ) : (
              <WelcomeDashboard user={user} />
            )}
          </ProtectedRoute>
        } />
        
        {/* Client Dashboard Routes */}
        <Route path="/dashboard/*" element={
          <ProtectedRoute requireClient={true}>
            <ClientDashboard 
              user={user} 
              onLogout={handleLogout} 
            />
          </ProtectedRoute>
        } />
        
        {/* Admin Dashboard Routes */}
        <Route path="/admin" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminDashboard 
              user={user} 
              onLogout={handleLogout} 
            />
          </ProtectedRoute>
        } />
        
        <Route path="/admin/*" element={
          <ProtectedRoute requireAdmin={true}>
            <AdminDashboard 
              user={user} 
              onLogout={handleLogout} 
            />
          </ProtectedRoute>
        } />
        
        {/* 404 - Redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      {/* Goodbye Popup - Shows when logging out */}
      <GoodbyePopup 
        show={showGoodbye} 
        onClose={() => setShowGoodbye(false)}
        userName={logoutUserName}
      />
    </Router>
  );
}

export default App;